/**
 * In-app browser → external browser visitor handoff.
 *
 * When we bounce a user out of KakaoTalk/LINE/Instagram/... into the system
 * browser, that browser starts with empty storage and would mint a new
 * `la_vid`, splitting one person into two visitors and losing the in-app
 * acquisition source. The handoff URL carries the visitor id and the in-app
 * session's coarse attribution so the external browser can continue it.
 *
 * Only coarse campaign/referrer fields and the random visitor id travel —
 * never auth tokens or personal data. Pure helpers only (no storage access);
 * session.ts wires them to storage.
 */

import type { InAppBrowserType } from "@/lib/utils/in-app-browser";

export const MAX_ATTRIBUTION_LENGTH = 120;

export const HANDOFF_PARAM_KEYS = [
  "la_vid",
  "la_from",
  "la_src",
  "la_med",
  "la_cmp",
] as const;

export type HandoffFrom = Exclude<InAppBrowserType, "unknown">;

/** in-app browser type → source label used when the session itself is 'direct'. */
const IN_APP_SOURCE: Record<HandoffFrom, string> = {
  kakaotalk: "kakaotalk",
  line: "line",
  naver: "naver_app",
  instagram: "instagram",
  facebook: "facebook",
  threads: "threads",
  twitter: "x",
  daum: "daum_app",
  "android-webview": "android_webview",
};

export const IN_APP_MEDIUM = "inapp";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// session.ts createId() fallback when crypto.randomUUID is unavailable.
const FALLBACK_ID_PATTERN = /^\d{12,14}-[0-9a-z]{1,16}$/;

export function cleanAttributionValue(value: string | null | undefined): string | null {
  const cleaned = value?.trim().slice(0, MAX_ATTRIBUTION_LENGTH) ?? "";
  return cleaned || null;
}

export function isValidVisitorId(value: string | null | undefined): value is string {
  return Boolean(value && (UUID_PATTERN.test(value) || FALLBACK_ID_PATTERN.test(value)));
}

export function isHandoffFrom(value: string | null | undefined): value is HandoffFrom {
  return Boolean(value && Object.prototype.hasOwnProperty.call(IN_APP_SOURCE, value));
}

export function inAppSourceFor(from: HandoffFrom): string {
  return IN_APP_SOURCE[from];
}

export interface HandoffInput {
  visitorId: string | null;
  from: HandoffFrom;
  /** Current session's entry attribution (null when unknown). */
  source: string | null;
  medium: string | null;
  campaign: string | null;
}

/** Builds the handoff query params. 'direct' (or unknown) becomes the in-app source. */
export function buildHandoffParams(input: HandoffInput): Record<string, string> {
  const params: Record<string, string> = { la_from: input.from };
  if (isValidVisitorId(input.visitorId)) params.la_vid = input.visitorId;

  const source = cleanAttributionValue(input.source);
  const isDirect = !source || source === "direct";
  params.la_src = isDirect ? inAppSourceFor(input.from) : source;

  const medium = isDirect ? IN_APP_MEDIUM : cleanAttributionValue(input.medium);
  if (medium) params.la_med = medium;

  const campaign = cleanAttributionValue(input.campaign);
  if (campaign) params.la_cmp = campaign;

  return params;
}

/** Returns targetUrl with the handoff params set (existing la_* values replaced). */
export function appendHandoffParams(
  targetUrl: string,
  params: Record<string, string>,
): string {
  try {
    const url = new URL(targetUrl);
    for (const key of HANDOFF_PARAM_KEYS) url.searchParams.delete(key);
    for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);
    return url.toString();
  } catch {
    return targetUrl;
  }
}

export interface IncomingHandoff {
  visitorId: string | null;
  from: HandoffFrom | null;
  source: string | null;
  medium: string | null;
  campaign: string | null;
}

/** Parses and validates handoff params. null when the URL is not a handoff. */
export function readHandoffParams(params: URLSearchParams): IncomingHandoff | null {
  const rawVid = params.get("la_vid");
  const rawFrom = params.get("la_from");
  const visitorId = isValidVisitorId(rawVid) ? rawVid : null;
  const from = isHandoffFrom(rawFrom) ? rawFrom : null;
  if (!visitorId && !from) return null;

  const source = cleanAttributionValue(params.get("la_src"));
  return {
    visitorId,
    from,
    source,
    medium: source ? cleanAttributionValue(params.get("la_med")) : null,
    campaign: source ? cleanAttributionValue(params.get("la_cmp")) : null,
  };
}

/**
 * Decides whether the external browser adopts the incoming visitor id.
 * A browser without its own (unexpired) first touch has no history, so the
 * incoming id becomes its la_vid. Otherwise the local id is kept and the
 * incoming one is reported as handoff_visitor_id.
 */
export function resolveHandoffVisitor(input: {
  incomingVisitorId: string | null;
  localVisitorId: string | null;
  hasFirstTouch: boolean;
}): { adopt: boolean; handoffVisitorId: string | null } {
  const { incomingVisitorId, localVisitorId, hasFirstTouch } = input;
  if (!incomingVisitorId || incomingVisitorId === localVisitorId) {
    return { adopt: false, handoffVisitorId: null };
  }
  if (!hasFirstTouch) return { adopt: true, handoffVisitorId: null };
  return { adopt: false, handoffVisitorId: incomingVisitorId };
}

/**
 * Returns pathname+search+hash without the handoff params, or null when there
 * is nothing to strip. Other params and the hash are preserved.
 */
export function stripHandoffParams(href: string): string | null {
  try {
    const url = new URL(href);
    let changed = false;
    for (const key of HANDOFF_PARAM_KEYS) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (!changed) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}
